import React, { useEffect, useState } from "react";
import "./resellerStudio.css";
import notifications from "../../common/Components/Notifications/Notification";
import type { CatalogueSharingMode } from "../../types/catalogue.types";
import { setCatalogueSharingMode } from "./catalogueResellerApi";

const MODES: { value: CatalogueSharingMode; title: string; detail: string }[] = [
  {
    value: "EXCLUSIVE",
    title: "Exclusive",
    detail: "New links open only for the number you invite.",
  },
  {
    value: "RESELLER",
    title: "Reseller",
    detail: "New links start as reseller shares. Pick Exclusive or Open for anyone else.",
  },
  {
    value: "OPEN",
    title: "Open",
    detail: "New links open without a code. Prices still follow the link.",
  },
];

type Props = {
  catalogueId: string;
  value?: CatalogueSharingMode;
  onChange?: (mode: CatalogueSharingMode) => void;
  compact?: boolean;
};

/** Studio: the starting choice for new links. Links already sent keep their own. Saved on tap. */
const CatalogueSharingModeField: React.FC<Props> = ({ catalogueId, value = "EXCLUSIVE", onChange, compact }) => {
  const [mode, setMode] = useState<CatalogueSharingMode>(value);
  const [saving, setSaving] = useState<CatalogueSharingMode | null>(null);

  useEffect(() => setMode(value), [value]);

  const choose = async (next: CatalogueSharingMode) => {
    if (next === mode || saving) return;
    const previous = mode;
    setMode(next);
    setSaving(next);
    try {
      const res = await setCatalogueSharingMode(catalogueId, next);
      if (!res.success) throw new Error(res.message || "Could not save sharing mode.");
      onChange?.(next);
    } catch (e: any) {
      setMode(previous);
      notifications({ title: "Sharing", message: e?.message || "Could not save sharing mode.", type: "danger" });
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className={`hkp-studio-sharing${compact ? " is-compact" : ""}`}>
      <div className="hkp-studio-sharing-label" id={`sharing-${catalogueId}`}>
        Default for new links
      </div>
      <div className="hkp-studio-sharing-options" role="radiogroup" aria-labelledby={`sharing-${catalogueId}`}>
        {MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            role="radio"
            aria-checked={mode === m.value}
            className={`hkp-studio-sharing-option${mode === m.value ? " is-active" : ""}`}
            onClick={() => void choose(m.value)}
            disabled={Boolean(saving)}
          >
            <span className="hkp-studio-sharing-title">
              {m.title}
              {saving === m.value ? <span className="hkp-studio-sharing-saving"> · saving</span> : null}
            </span>
            {compact ? null : <span className="hkp-studio-sharing-detail">{m.detail}</span>}
          </button>
        ))}
      </div>
      {compact ? (
        <p className="hkp-studio-sharing-help">{MODES.find((m) => m.value === mode)?.detail}</p>
      ) : null}
      <p className="hkp-studio-sharing-help">
        Links already sent keep their own setting. Change one link from Shared links below.
      </p>
    </div>
  );
};

export default CatalogueSharingModeField;
